import type { Notification } from "@kontextwerk/control-web-client"
import { internalOrderTypeLabel } from "../../../../../../../_shared/_logic/order-type-labels"
import type { InboxCaseDetails, WorkOrderCandidate } from "./contract"
import { inboxCaseActionCaseIds } from "./inboxCaseTodo"

export const parseInboxCaseDetails = (todo: Notification): InboxCaseDetails => {
  const payload = (todo?.payload && typeof todo.payload === "object" ? todo.payload : {}) as Record<
    string,
    any
  >
  const descriptionLines = (todo?.description || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
  const findLineValue = (prefix: string) => {
    const entry = descriptionLines.find((line) =>
      line.toLowerCase().startsWith(prefix.toLowerCase()),
    )

    if (!entry) return null

    return entry.slice(prefix.length).trim() || null
  }
  const textPayloadValue = (key: string) =>
    typeof payload[key] === "string" && payload[key].trim() ? payload[key].trim() : null
  const actionCaseIds = inboxCaseActionCaseIds(todo)
  const slot =
    payload?.slot && typeof payload.slot === "object" && !Array.isArray(payload.slot)
      ? payload.slot
      : null
  const slotStart =
    typeof payload.slot_start_at === "string"
      ? payload.slot_start_at
      : typeof payload.slot_start === "string"
        ? payload.slot_start
        : typeof slot?.start_at === "string"
          ? slot.start_at
          : null
  const slotEnd =
    typeof payload.slot_end_at === "string"
      ? payload.slot_end_at
      : typeof payload.slot_end === "string"
        ? payload.slot_end
        : typeof slot?.end_at === "string"
          ? slot.end_at
          : null
  const toDateLabel = (value: string | null) => {
    if (!value) return null

    const parsed = new Date(value)

    if (Number.isNaN(parsed.getTime())) return null

    return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
  }
  const toTimeLabel = (value: string | null) => {
    if (!value) return null

    const parsed = new Date(value)

    if (Number.isNaN(parsed.getTime())) return null

    return parsed.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  }
  const toSlotLabel = (start: string | null, end: string | null) => {
    const startLabel = toDateLabel(start)

    if (!startLabel) return null

    const endLabel = toTimeLabel(end)

    return endLabel ? `${startLabel} - ${endLabel}` : startLabel
  }
  const slotLabel = findLineValue("Terminwunsch:") || toSlotLabel(slotStart, slotEnd)
  const normalizeCandidate = (candidate: any): WorkOrderCandidate | null => {
    if (!candidate || typeof candidate !== "object") return null

    const payload =
      candidate.payload && typeof candidate.payload === "object" ? candidate.payload : {}
    const workOrderId =
      typeof candidate.work_order_id === "string"
        ? candidate.work_order_id
        : typeof candidate.id === "string"
          ? candidate.id
          : typeof payload.work_order_id === "string"
            ? payload.work_order_id
            : ""

    if (!workOrderId) return null

    const candidateOrderTypeLabel =
      typeof candidate.order_type_label === "string"
        ? candidate.order_type_label
        : internalOrderTypeLabel(candidate)
    const label =
      typeof candidate.label === "string"
        ? candidate.label
        : typeof payload.order_label === "string"
          ? payload.order_label
          : [candidate.order_number, candidateOrderTypeLabel]
              .filter((part) => typeof part === "string" && part.trim())
              .join(" - ") || workOrderId

    return {
      workOrderId,
      label,
      propertyUnitId:
        typeof candidate.property_unit_id === "string"
          ? candidate.property_unit_id
          : typeof payload.property_unit_id === "string"
            ? payload.property_unit_id
            : null,
      propertyUnitLabel:
        typeof candidate.property_unit_label === "string"
          ? candidate.property_unit_label
          : typeof payload.property_unit_label === "string"
            ? payload.property_unit_label
            : null,
      payload,
    }
  }
  const candidateSources = [
    ...(Array.isArray(payload.work_order_candidates) ? payload.work_order_candidates : []),
    ...(Array.isArray(payload.work_order_candidate_summaries)
      ? payload.work_order_candidate_summaries
      : []),
  ]
  const candidates = candidateSources
    .map(normalizeCandidate)
    .filter(Boolean) as WorkOrderCandidate[]
  const workOrderUnresolved = Boolean(
    payload.work_order_unresolved ||
    payload.kind === "appointment_work_order_unresolved" ||
    (todo.event_type || "").includes("work_order_unresolved"),
  )
  const rawRequirements = Array.isArray(payload.action_case_requirements)
    ? payload.action_case_requirements
    : Array.isArray(payload.requirements)
      ? payload.requirements
      : []
  const reviewRequirements = rawRequirements
    .map((item) => (typeof item === "string" ? item : ""))
    .filter(Boolean)
  const flowKey = textPayloadValue("flow_key")
  const source = typeof payload.source === "string" ? payload.source : null
  const wrongRecipientReview = Boolean(
    flowKey === "wrong_recipient_replacement" ||
    source === "bot_appointments.wrong_recipient" ||
    source === "appointment_portal.wrong_recipient" ||
    reviewRequirements.includes("REPLACE_OR_REMOVE_WRONG_RECIPIENT_CUSTOMER") ||
    (todo.event_type || "").includes("wrong_recipient"),
  )
  const contactRecipientReview = Boolean(
    flowKey === "customer_contact_verification" ||
    flowKey === "customer_contact_point_review" ||
    reviewRequirements.includes("REVIEW_CONTACT_POINT") ||
    reviewRequirements.includes("UPDATE_SOURCE_CONTACT_DATA"),
  )
  const customerName =
    textPayloadValue("customer_name") || findLineValue("Kunde:") || "Unbekannter Ansprechpartner"
  const contactEmail = textPayloadValue("contact_email") || findLineValue("E-Mail:") || ""
  const contactPhone = textPayloadValue("contact_phone") || findLineValue("Telefon:") || ""
  const suggestedContact =
    payload.suggested_contact &&
    typeof payload.suggested_contact === "object" &&
    !Array.isArray(payload.suggested_contact)
      ? payload.suggested_contact
      : null
  const suggestedValue = (key: string) => {
    const value = suggestedContact?.[key]

    return typeof value === "string" && value.trim() ? value.trim() : null
  }
  const suggestedRole = textPayloadValue("suggested_role")
  const customerRoleHint = textPayloadValue("customer_role_hint")

  return {
    customerId: textPayloadValue("customer_id") || textPayloadValue("verified_customer_id"),
    customerName,
    contactEmail,
    contactPhone,
    collectedContactName: suggestedValue("display_name") || suggestedValue("name"),
    collectedContactEmail: suggestedValue("email") || contactEmail || null,
    collectedContactPhone: suggestedValue("phone") || contactPhone || null,
    collectedContactRole: suggestedRole || customerRoleHint,
    customerRoleHint,
    workOrderId: typeof payload.work_order_id === "string" ? payload.work_order_id : null,
    workOrderLabel: findLineValue("Auftrag:"),
    inboxCaseId: actionCaseIds[0] ?? null,
    scheduleEventId:
      typeof payload.schedule_event_id === "string" ? payload.schedule_event_id : null,
    propertyId: typeof payload.property_id === "string" ? payload.property_id : null,
    propertyUnitId: typeof payload.property_unit_id === "string" ? payload.property_unit_id : null,
    propertyUnitLabel:
      typeof payload.property_unit_label === "string"
        ? payload.property_unit_label
        : findLineValue("Einheit:"),
    notes: typeof payload.notes === "string" ? payload.notes : findLineValue("Notiz:"),
    slot,
    slotStart,
    slotEnd,
    slotLabel,
    descriptionLines,
    source,
    workOrderUnresolved,
    workOrderCandidates: candidates,
    referenceDurationMinutes:
      typeof payload.reference_duration_minutes === "number"
        ? payload.reference_duration_minutes
        : null,
    workOrderResolutionReason:
      typeof payload.work_order_resolution_reason === "string"
        ? payload.work_order_resolution_reason
        : null,
    workOrderResolutionConfidence:
      typeof payload.work_order_resolution_confidence === "string"
        ? payload.work_order_resolution_confidence
        : null,
    workOrderResolutionScore:
      typeof payload.work_order_resolution_score === "number"
        ? payload.work_order_resolution_score
        : null,
    workOrderResolutionSignals: Array.isArray(payload.work_order_resolution_signals)
      ? payload.work_order_resolution_signals
          .map((item) => (typeof item === "string" ? item : ""))
          .filter(Boolean)
      : [],
    reviewRequirements,
    flowKey,
    contactRecipientReview,
    wrongRecipientReview,
    userStatement: typeof payload.user_statement === "string" ? payload.user_statement : null,
    suggestedContact,
    suggestedRole,
    contactOrderCanceled: Boolean(payload.contact_order_canceled),
    contactStepRescheduled: Boolean(payload.contact_step_rescheduled),
    contactStepResultRecorded: Boolean(payload.contact_step_result_recorded),
    providerCalendarId:
      typeof payload.provider_calendar_id === "string" ? payload.provider_calendar_id : null,
    providerEventId:
      typeof payload.provider_event_id === "string" ? payload.provider_event_id : null,
    employeeLabel: typeof payload.employee_label === "string" ? payload.employee_label : null,
    addressLabel:
      textPayloadValue("address") ||
      textPayloadValue("property_label") ||
      textPayloadValue("property_address") ||
      findLineValue("Adresse:"),
  }
}

export const withSelectedWorkOrder = (
  details: InboxCaseDetails,
  candidate: WorkOrderCandidate | null,
): InboxCaseDetails => ({
  ...details,
  workOrderId: candidate?.workOrderId ?? details.workOrderId,
  workOrderLabel: candidate?.label ?? details.workOrderLabel,
  propertyUnitId: candidate?.propertyUnitId ?? details.propertyUnitId,
  propertyUnitLabel: candidate?.propertyUnitLabel ?? details.propertyUnitLabel,
  workOrderUnresolved: candidate ? false : details.workOrderUnresolved,
})
